import type { AdminAppealEvidenceRefDto } from '@carcheck/shared';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';

import { tokens } from '../../../design';

/**
 * Single evidence thumbnail for the appeal detail drawer (AUT-132).
 * Mirrors the mockup `.evidence-thumb` shape: 88×64 rounded tile with a
 * `фото` / `документ` tag pill pinned bottom-left.
 *
 *   - `kind: 'photo'`    → `<img>` cover preview; click opens the lightbox.
 *   - `kind: 'document'` → a document icon surface; click opens the signed
 *     `url` in a new tab (`rel="noopener"`).
 *
 * `onLoadError` bubbles a photo `<img>` 403/404 (signed-URL expiry) to the
 * parent so it can surface the "обновите апелляцию" hint — the detail
 * re-fetch re-mints fresh 5-min URLs. The raw S3 key is never on the wire
 * (the DTO carries only the minted URL).
 */
export interface EvidenceTileProps {
  evidence: AdminAppealEvidenceRefDto;
  /** Accessible label + tag text, e.g. "фото 1" / "документ 1". */
  label: string;
  /** Tag pill caption (the kind word, e.g. "фото"). */
  tag: string;
  onClick: () => void;
  onLoadError?: () => void;
}

const TILE_SX = {
  position: 'relative',
  width: 88,
  height: 64,
  borderRadius: `${tokens.radius.card}px`,
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.default',
  color: tokens.text.tertiary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

/** Bottom-left rounded kind pill (фото / документ). */
const TileTag = ({ tag }: { tag: string }): ReactElement => (
  <Typography
    component="span"
    sx={{
      position: 'absolute',
      bottom: 4,
      left: 4,
      fontSize: 9,
      fontWeight: 700,
      lineHeight: 1.2,
      color: '#fff',
      bgcolor: 'rgba(12, 20, 15, 0.7)',
      px: 0.625,
      py: '1px',
      borderRadius: 999,
    }}
  >
    {tag}
  </Typography>
);

export const EvidenceTile = ({
  evidence,
  label,
  tag,
  onClick,
  onLoadError,
}: EvidenceTileProps): ReactElement => (
  <ButtonBase onClick={onClick} aria-label={label} sx={TILE_SX}>
    {evidence.kind === 'photo' ? (
      <Box
        component="img"
        src={evidence.url}
        alt={label}
        loading="lazy"
        decoding="async"
        onError={onLoadError}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : (
      <DescriptionOutlinedIcon fontSize="small" />
    )}
    <TileTag tag={tag} />
  </ButtonBase>
);
